import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useModal } from "../../contexts/ModalContext";
import { Box, Typography, Paper, Container, Button, CircularProgress } from "@mui/material";
import api from "../../services/api";
// Import icons
import PendingIcon from '@mui/icons-material/Pending';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import EmailIcon from '@mui/icons-material/Email';

const AccountStatus = () => {
  const { account_id } = useParams(); // Get account_id from URL params
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const { openSuccessModal, openErrorModal, closeModal } = useModal();
  const [apiCallMade, setApiCallMade] = useState(false);

  const statusConfig = {
    PE: {
      title: "Account Under Review",
      message: "Your account is currently being reviewed by our team. You will be notified once the verification process is complete.",
      subMessage: "This usually takes 1-2 business days. Thank you for your patience.",
      icon: PendingIcon,
      color: "#FFA726" // Orange
    },
    SU: {
      title: "Account Suspended",
      message: "Your account has been suspended due to policy and compliance violations.",
      subMessage: "If you believe this is a mistake, please contact our support team for assistance.",
      icon: BlockIcon,
      color: "#D32F2F" // Red
    },
    AP: {
      title: "Account Approved",
      message: "Your account has been successfully verified!",
      subMessage: "You can now log in and start using SalesGro Analytics.",
      icon: CheckCircleIcon,
      color: "#2E7D32" // Green
    }
  };

  // Use useCallback to prevent recreation of this function on each render
  const checkAccountStatus = useCallback(async () => {
    if (apiCallMade) return; // Prevent multiple API calls
    
    try {
      setLoading(true);
      setApiCallMade(true); // Mark that API call has been made
      console.log("Checking status for account ID:", account_id);
      
      const result = await api.get(`/account/status/${account_id}/`);
      const data = result.data;
      console.log("data ", data)
      setStatus(data.account_status);
      
      if (data.account_status === 'AP') {
        openSuccessModal(
          "Congratulations", 
          "Your account has been successfully verified, and the 7-Day Free Plan has been activated. Enjoy the SalesGro Analytics features and elevate your customer engagement!"
        );
      }
      setLoading(false);
    } catch (error) {
      console.error("Error checking account status:", error);
      const errorMessage = error.response?.data?.message || "Failed to verify account status";
      openErrorModal("Status Check Failed", errorMessage);
      setLoading(false);
    }
  }, [account_id, navigate, apiCallMade]); // Only depend on account_id, navigate and apiCallMade

  useEffect(() => {
    if (account_id && !apiCallMade) {
      checkAccountStatus();
    } else if (!account_id) {
      setLoading(false);
      openErrorModal("Invalid Request", "No account ID provided");
    }
  }, [account_id, checkAccountStatus, apiCallMade]); // Only depend on account_id and the checkAccountStatus function

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  const currentStatus = statusConfig[status] || statusConfig.PE;
  const StatusIcon = currentStatus.icon;

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          textAlign: 'center',
          backgroundColor: '#fff',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <StatusIcon 
            sx={{ 
              fontSize: 64, 
              color: currentStatus.color,
              mb: 2
            }} 
          />
          
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            fontWeight="medium"
            sx={{ color: currentStatus.color }}
          >
            {currentStatus.title}
          </Typography>
          
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {currentStatus.message}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              mt: 2,
              fontStyle: 'italic'
            }}
          >
            {currentStatus.subMessage}
          </Typography>
        </Box>

        <Box 
          sx={{ 
            mt: 3,
            p: 2,
            backgroundColor: '#FFF4F4',
            borderRadius: 1,
            border: '1px solid #FFE0E0'
          }}
        >
          <Typography variant="body2" color="error">
            For any assistance, please contact our support team at{' '}
            <a href="mailto:hello@salesgro.ai" style={{ color: '#D32F2F' }}>
            hello@salesgro.ai
            </a>
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.open('https://salesgro.ai/contact', '_blank')}
            sx={{ 
              mt: 2, 
              mb: 2, 
              py: 1,
              background: 'linear-gradient(45deg, #007050 30%, #00a070 90%)',
              boxShadow: '0 3px 5px 2px rgba(0, 112, 80, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #00513a 30%, #007050 90%)',
                boxShadow: '0 3px 5px 2px rgba(0, 81, 58, .3)',
              },
            }}
          >
            Contact Support
          </Button>
        </Box>

        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/auth/login')}
          sx={{ 
            mt: 3,
            color: 'text.secondary',
            borderColor: 'text.secondary',
            '&:hover': {
              borderColor: 'text.primary',
              color: 'text.primary',
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Back to Login
        </Button>
      </Paper>
    </Container>
  );
};

export default AccountStatus;